import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Col, FormGroup, Label, Button, Input, FormFeedback } from 'reactstrap';
import { BASE_URL, API, ESTADOS_OPORTUNIDAD } from 'CONST';
import { patch, post } from 'helpers/requests';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CheckModal from '../shared/CheckModal';
import WarningModal from '../UI/WarningModal';
import AvanzarOportunidadConfirmationModal from './AvanzarOportunidadConfirmationModal';

const ExposicionMaximaSection = ({ fullData, id, estadosOportunidad, observaciones }) => {
  const history = useHistory();
  const [exposicionMaxima, setExposicionMaxima] = useState();
  const [observacion, setObservacion] = useState('');
  const [observacionesEstado, setObservacionesEstado] = useState([]);
  const [editableSection, setEditableSection] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const editableStates = [ESTADOS_OPORTUNIDAD.evaluacionAprobada];

  useEffect(() => {
    if (fullData && fullData.estado && editableStates.includes(fullData.estado.nemotecnico)) {
      setEditableSection(true);
    }
    if (fullData && fullData.exposicion_maxima) {
      setExposicionMaxima(fullData.exposicion_maxima);
    }
  }, [fullData]);

  useEffect(() => {
    if (observaciones) {
      setObservacionesEstado(
        observaciones.filter(
          (obs) => obs.estado.nemotecnico === ESTADOS_OPORTUNIDAD.exposicionMaximaHabilitada,
        ),
      );
    }
  }, [observaciones, fullData]);

  const postObservacion = () => {
    if (observacion) {
      const bodyObservacion = {
        oportunidad_id: id,
        estado_id: estadosOportunidad.find(
          (e) => e.nemotecnico === ESTADOS_OPORTUNIDAD.exposicionMaximaHabilitada,
        ).id,
        observacion,
      };
      post(`${BASE_URL}${API.observacionesOportunidad}`, bodyObservacion)
        .then(() => {
          setShowUpdateModal(true);
        })
        .catch(() => {
          setShowErrorModal(true);
        });
    }
  };

  const avanzarOportunidad = () => {
    setLoading(true);
    const body = {
      exposicion_maxima: exposicionMaxima,
      estado_id: estadosOportunidad.find(
        (e) => e.nemotecnico === ESTADOS_OPORTUNIDAD.exposicionMaximaHabilitada,
      ).id,
    };
    patch(`${BASE_URL}${API.oportunidadesLinea}${id}/`, body)
      .then(() => {
        postObservacion();
        setShowUpdateModal(true);
      })
      .catch(() => {
        setShowErrorModal(true);
      })
      .finally(() => {
        setConfirmationModal(false);
        setLoading(false);
      });
  };

  return (
    <div>
      <FormGroup row>
        <Col sm={2}>
          <Label>
            Exposición Máxima (UF)
            <span className="required text-danger">*</span>
          </Label>
        </Col>
        <Col sm={5}>
          <NumberFormat
            className="form-control"
            decimalScale="2"
            decimalSeparator="."
            value={exposicionMaxima}
            onValueChange={(values) => setExposicionMaxima(values.floatValue)}
            allowNegative={false}
            disabled={!editableSection}
          />
          {exposicionMaxima !== undefined && fullData.monto && exposicionMaxima < fullData.monto && (
            <FormFeedback style={{ fontSize: '12px', margin: '0 0 5px 5px' }}>
              Debe solicitar una exposición máxima mayor o igual que la línea. &#40;
              {fullData.monto}
              &nbsp;UF&#41;
            </FormFeedback>
          )}
        </Col>
      </FormGroup>
      <FormGroup row>
        <Col sm={2}>
          <Label>Observaciones</Label>
        </Col>
        <Col>
          <Input
            id="observacion-info-inicial"
            type="textarea"
            value={
              !editableSection && observacionesEstado.length > 0
                ? observacionesEstado[0].observacion
                : observacion || ''
            }
            onChange={(event) => setObservacion(event.target.value)}
            rows="3"
            disabled={!editableSection}
          />
        </Col>
      </FormGroup>
      {observacionesEstado.slice(!editableSection).map((obs) => {
        return (
          <FormGroup row key={obs.id}>
            <Col sm={2} />
            <Col>
              <Input type="textarea" value={obs.observacion} rows="3" disabled />
            </Col>
          </FormGroup>
        );
      })}
      {editableSection && (
        <div className="button-container d-flex justify-content-end">
          <Button
            className="btn btn-secondary blue"
            onClick={() => setConfirmationModal(true)}
            disabled={
              !exposicionMaxima ||
              !fullData.monto ||
              exposicionMaxima < fullData.monto ||
              confirmationModal
            }
          >
            {loading ? <FontAwesomeIcon spin={loading} icon={faSpinner} /> : 'Guardar Información'}
          </Button>
        </div>
      )}
      <AvanzarOportunidadConfirmationModal
        openModal={confirmationModal}
        setOpenModal={setConfirmationModal}
        avanzarOportunidad={avanzarOportunidad}
        loading={loading}
      />
      <CheckModal
        isOpen={showUpdateModal}
        toggle={() => history.push('/garantia/oportunidades')}
        handleOk={() => history.push('/garantia/oportunidades')}
        okBtnText="OK"
        confirmMsg="Exposición máxima habilitada exitosamente."
      />
      <WarningModal
        isOpen={showErrorModal}
        toggle={() => setShowErrorModal(false)}
        handleClearError={() => setShowErrorModal(false)}
        errorMsg="Ha ocurrido un error al habilitar la exposición máxima."
        loading={false}
        onlyCancelButton
      />
    </div>
  );
};

ExposicionMaximaSection.propTypes = {
  id: PropTypes.string,
  fullData: PropTypes.shape({
    afianzado: PropTypes.shape({
      id: PropTypes.number,
    }),
    estado: PropTypes.shape({
      id: PropTypes.number,
      nemotecnico: PropTypes.string,
    }),
    id: PropTypes.number,
    monto: PropTypes.number,
    exposicion_maxima: PropTypes.number,
  }),
  estadosOportunidad: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      nemotecnico: PropTypes.string,
    }),
  ),
  observaciones: PropTypes.arrayOf(
    PropTypes.shape({
      estado: PropTypes.shape({
        id: PropTypes.number,
        nemotecnico: PropTypes.string,
      }),
      id: PropTypes.number,
    }),
  ),
};

ExposicionMaximaSection.defaultProps = {
  fullData: null,
  id: null,
  estadosOportunidad: [],
  observaciones: [],
};

export default ExposicionMaximaSection;
